import { CollisionSensitivity, DetectionZone, EventTrigger, EventTriggerType, FleetDefaults } from "@app/shared/src/models";
import { MPS_TO_MPH } from "@app/shared/src/components_v2";
import { RelayWiringMap, effectiveRelayWiring } from "@app/shared/src/helpers/relayWiring";
import { PassSide, sideOfZoneName } from "@app/shared/src/helpers/zoneNameHelpers";
import { lanesFromZonePoints } from "@app/shared/src/helpers/sidePassGeometry";

/**
 * Alerts and relays for one truck, gathered from the three places they actually live.
 *
 * This is the section that makes "a device's settings" a fiction worth naming: the collision
 * thresholds are columns on a trigger, the relay pulses are a JSON blob on a trigger→endpoint join,
 * and the side-pass width is the geometry of a detection zone. A director changes one thing; three
 * different rows move. Reading them into one flat record is what lets the same equality check that
 * governs radar and video govern this too.
 *
 * Field meanings follow each trigger's own convention, which is not self-describing on the column
 * names — see ImpendingZoneEntryEventTrigger and SpeedingEventTrigger:
 *
 *   impending collision   MinValue = time to collision (s), AdditionalValue = min approach speed (m/s)
 *   speeding              MaxValue = the speed limit (m/s) — see speedingLimitOf
 *
 * MaxValue on the collision trigger is unused by the firmware and carries the sensitivity until the
 * device learns to read it. See SENSITIVITY_FIELD below.
 */

export type AlertSettings = {
    minApproachSpeedMps: number;
    reactionTimeSeconds: number;
    sensitivity: CollisionSensitivity;

    lightsPulseSeconds: number;
    hornPulseSeconds: number;

    speedingEnabled: boolean;
    speedingThresholdMps: number;

    leftPassEnabled: boolean;
    rightPassEnabled: boolean;
    sidePassMinSpeedMps: number;
    leftPassLanes: number;
    rightPassLanes: number;
};

/**
 * Sensitivity rides the collision trigger's MaxValue.
 *
 * The column is free on an impending trigger (no speed ceiling), and the firmware reads it from
 * there: the ordinal maps to how many consecutive collision-course detections are required before
 * firing — Highest fires on one, Lowest waits for five, Medium is the historical three. Older
 * firmware ignores the column and behaves as Medium. See Shared/CollisionSensitivity.cs for the one
 * place the mapping lives.
 *
 * No minimum version is stated because none is sourced anywhere in Shared or CoreApi.
 */
export const SENSITIVITY_FIELD = "maxValue" as const;

/**
 * The standard build's terminals, kept only as the fallback wiring. Which relay is actually which
 * is configurable per fleet (FleetDefaults.hornRelayNumber…) and per truck (WebRelay endpoint
 * overrides) — use fleetRelayWiring / effectiveRelayWiring where a real record is in hand.
 */
export const LIGHTS_RELAY = 2;
export const HORN_RELAY = 1;

/** The fleet's stated wiring, straight off its defaults record. */
export function fleetRelayWiring(defaults: FleetDefaults | undefined): RelayWiringMap {
    return effectiveRelayWiring(defaults);
}

/**
 * The triggers this truck owns, out of everything that governs it.
 *
 * GetByFacilityId also returns org-wide triggers — an empty facility join means "every facility" —
 * so anything editing a trigger must narrow to the ones bound here first. Writing an inherited one
 * retunes every truck it governs; for a side pass it repoints another truck's alert at this zone.
 */
export function triggersForFacility(triggers: EventTrigger[], facilityId: string | undefined): EventTrigger[] {
    if (!facilityId) return [];
    return triggers.filter((t) => (t.eventTriggerFacilities ?? []).some((f) => f.facilityId === facilityId));
}

/** The collision trigger is the one impending-entry trigger that does not watch a side-pass zone. */
export function findCollisionTrigger(triggers: EventTrigger[]): EventTrigger | undefined {
    return triggers.find(
        (t) => t.triggerType === EventTriggerType.ImpendingZoneEntry && !sideOfZoneName(t.detectionZone?.name),
    );
}

export function findSidePassTrigger(triggers: EventTrigger[], side: PassSide): EventTrigger | undefined {
    return triggers.find(
        (t) => t.triggerType === EventTriggerType.ImpendingZoneEntry && sideOfZoneName(t.detectionZone?.name) === side,
    );
}

export function findSpeedingTrigger(triggers: EventTrigger[]): EventTrigger | undefined {
    return triggers.find((t) => t.triggerType === EventTriggerType.SpeedingEvent);
}

/**
 * The speed limit on a speeding trigger.
 *
 * The device's speeding detector fires when a vehicle is OUTSIDE the trigger's [MinValue, MaxValue]
 * band — the dashboard's "Minimum Speed" and "Maximum Speed" fields — so the limit is MaxValue and
 * MinValue is a floor. Earlier builds of this console wrote the limit to MinValue and left MaxValue
 * empty, which the device read as "alert on everything SLOWER than the limit": fleets got speeding
 * events for traffic well under it and none for traffic over it.
 *
 * A trigger in that shape still reads back as the number the director typed, because that is what
 * they meant; isSpeedingLimitMisplaced says it needs repairing, and setSpeedingLimit repairs it.
 */
export function speedingLimitOf(trigger: EventTrigger | undefined): number | undefined {
    if (!trigger) return undefined;
    return trigger.maxValue ?? trigger.minValue ?? undefined;
}

/** A speeding trigger whose limit sits in MinValue with no MaxValue — firing below the limit. */
export function isSpeedingLimitMisplaced(trigger: EventTrigger | undefined): boolean {
    return trigger != null && trigger.maxValue == null && trigger.minValue != null;
}

/**
 * Write the limit where the device reads it, and clear the floor a misplaced one left behind —
 * leaving MinValue set would keep the trigger firing on slow traffic no matter what MaxValue says.
 */
export function setSpeedingLimit(trigger: EventTrigger, limitMps: number): void {
    trigger.maxValue = limitMps;
    trigger.minValue = undefined;
}

/** A trigger with no explicit IsEnabled predates the field and is running — never test `=== true`. */
export const isTriggerEnabled = (trigger: EventTrigger | undefined): boolean => trigger?.isEnabled !== false;

type WebRelayConfig = { relay?: number; Relay?: number; pulseTime?: number; PulseTime?: number };
type WebRelayEndpointConfig = { relays?: WebRelayConfig[]; Relays?: WebRelayConfig[] };

function parseRelayConfig(raw: string | undefined): WebRelayEndpointConfig | undefined {
    if (!raw) return undefined;
    try {
        return JSON.parse(raw) as WebRelayEndpointConfig;
    } catch {
        // A device-authored blob we cannot read is not something to guess at.
        return undefined;
    }
}

/** Pulse seconds configured for a relay terminal on this trigger, if any. */
export function relayPulseSeconds(trigger: EventTrigger | undefined, relayNumber: number): number | undefined {
    for (const join of trigger?.eventTriggerClientEndpoints ?? []) {
        const cfg = parseRelayConfig(join.endpointConfig);
        const relays = cfg?.relays ?? cfg?.Relays ?? [];
        const match = relays.find((r) => (r.relay ?? r.Relay) === relayNumber);
        const pulse = match?.pulseTime ?? match?.PulseTime;
        if (pulse != null) return pulse;
    }
    return undefined;
}

/**
 * Write a relay's pulse duration onto every join that drives that terminal.
 *
 * Returns whether anything changed, so a caller can skip a pointless save. The stored blob's casing
 * is preserved because the device parses it, not us.
 */
export function setRelayPulseSeconds(trigger: EventTrigger, relayNumber: number, seconds: number): boolean {
    let touched = false;
    for (const join of trigger.eventTriggerClientEndpoints ?? []) {
        const cfg = parseRelayConfig(join.endpointConfig);
        const relays = cfg?.relays ?? cfg?.Relays;
        if (!cfg || !relays) continue;

        const match = relays.find((r) => (r.relay ?? r.Relay) === relayNumber);
        if (!match) continue;

        if (match.PulseTime !== undefined) match.PulseTime = seconds;
        else match.pulseTime = seconds;

        join.endpointConfig = JSON.stringify(cfg);
        touched = true;
    }
    return touched;
}

/** Lanes a side-pass zone currently covers, read back off its width. */
export function lanesOfSidePassZone(zone: DetectionZone | undefined, fallback: number): number {
    return lanesFromZonePoints(zone?.points as { x: number; y: number }[] | undefined) ?? fallback;
}

/**
 * What this truck is actually running, with the fleet's values standing in where a trigger is absent.
 *
 * `wiring` says which terminal is which for THIS truck; defaulted from the fleet record so callers
 * without the truck's relay endpoint in hand still read correctly for every non-rewired truck.
 */
export function readAlertSettings(
    triggers: EventTrigger[],
    defaults: FleetDefaults,
    wiring: RelayWiringMap = effectiveRelayWiring(defaults),
): AlertSettings {
    const collision = findCollisionTrigger(triggers);
    const speeding = findSpeedingTrigger(triggers);
    const left = findSidePassTrigger(triggers, "left");
    const right = findSidePassTrigger(triggers, "right");

    return {
        minApproachSpeedMps: collision?.additionalValue ?? defaults.minimumApproachSpeedMps,
        reactionTimeSeconds: collision?.minValue ?? defaults.driverReactionTimeSeconds,
        sensitivity: (collision?.[SENSITIVITY_FIELD] as CollisionSensitivity | undefined) ?? defaults.sensitivity,

        lightsPulseSeconds: relayPulseSeconds(collision, wiring.lights.number) ?? defaults.lightsPulseSeconds,
        hornPulseSeconds: relayPulseSeconds(collision, wiring.horn.number) ?? defaults.hornPulseSeconds,

        // An absent trigger is an alert this truck cannot raise, which is not the same as one turned
        // off — but for the purpose of "what is this unit doing", both mean no alert fires.
        speedingEnabled: speeding ? isTriggerEnabled(speeding) : false,
        speedingThresholdMps: speedingLimitOf(speeding) ?? defaults.speedingThresholdMps,

        leftPassEnabled: left ? isTriggerEnabled(left) : false,
        rightPassEnabled: right ? isTriggerEnabled(right) : false,
        sidePassMinSpeedMps: left?.additionalValue ?? right?.additionalValue ?? defaults.sidePassMinSpeedMps,
        leftPassLanes: lanesOfSidePassZone(left?.detectionZone, defaults.leftPassLanes),
        rightPassLanes: lanesOfSidePassZone(right?.detectionZone, defaults.rightPassLanes),
    };
}

/** What the fleet says this truck should be running. */
export function alertSettingsFromDefaults(defaults: FleetDefaults): AlertSettings {
    return {
        minApproachSpeedMps: defaults.minimumApproachSpeedMps,
        reactionTimeSeconds: defaults.driverReactionTimeSeconds,
        sensitivity: defaults.sensitivity,

        lightsPulseSeconds: defaults.lightsPulseSeconds,
        hornPulseSeconds: defaults.hornPulseSeconds,

        speedingEnabled: defaults.speedingEnabled,
        speedingThresholdMps: defaults.speedingThresholdMps,

        leftPassEnabled: defaults.leftPassEnabled,
        rightPassEnabled: defaults.rightPassEnabled,
        sidePassMinSpeedMps: defaults.sidePassMinSpeedMps,
        leftPassLanes: defaults.leftPassLanes,
        rightPassLanes: defaults.rightPassLanes,
    };
}

/**
 * Numeric fields are compared at the precision a director edits them at, not as raw floats.
 *
 * Speeds are stored in m/s and typed in whole mph, so most fleet defaults cannot be represented
 * exactly by the control that edits them: 15.65 m/s displays as 35 mph and comes back as 15.6464.
 * Under exact equality — or any honest epsilon, since the worst-case error is half an mph, 0.22 m/s
 * — opening a section and pressing Save would permanently mark a truck "overridden" against a value
 * a director never changed, and offer to reset it to the number it already holds.
 *
 * So two values are the same setting when they are identical in the units shown on screen. A
 * difference the UI cannot display is not a difference the UI should report.
 *
 * MPS_TO_MPH is imported rather than redefined: the comparison has to round exactly the way the
 * inputs do, and a second copy of the constant would drift from the first.
 */
const comparisonPrecision: Partial<Record<keyof AlertSettings, (value: number) => number>> = {
    // Entered as whole mph.
    minApproachSpeedMps: (mps) => Math.round(mps * MPS_TO_MPH),
    speedingThresholdMps: (mps) => Math.round(mps * MPS_TO_MPH),
    sidePassMinSpeedMps: (mps) => Math.round(mps * MPS_TO_MPH),
    // Entered as seconds to one decimal place.
    reactionTimeSeconds: (s) => Math.round(s * 10),
    lightsPulseSeconds: (s) => Math.round(s * 10),
    hornPulseSeconds: (s) => Math.round(s * 10),
};

export function alertSettingsMatch(current: AlertSettings, defaults: AlertSettings): boolean {
    return alertSettingsDifferences(current, defaults).length === 0;
}

/** The fields that differ, so "overridden" can say what it means. */
export function alertSettingsDifferences(current: AlertSettings, defaults: AlertSettings): (keyof AlertSettings)[] {
    const keys = Object.keys(defaults) as (keyof AlertSettings)[];
    return keys.filter((key) => {
        const a = current[key];
        const b = defaults[key];

        const round = comparisonPrecision[key];
        if (round && typeof a === "number" && typeof b === "number") return round(a) !== round(b);

        return a !== b;
    });
}

export const ALERT_FIELD_LABELS: Record<keyof AlertSettings, string> = {
    minApproachSpeedMps: "minimum approach speed",
    reactionTimeSeconds: "driver reaction time",
    sensitivity: "sensitivity",
    lightsPulseSeconds: "lights pulse",
    hornPulseSeconds: "horn pulse",
    speedingEnabled: "speeding alert",
    speedingThresholdMps: "speeding threshold",
    leftPassEnabled: "left-side pass alert",
    rightPassEnabled: "right-side pass alert",
    sidePassMinSpeedMps: "side-pass minimum speed",
    leftPassLanes: "left-side pass width",
    rightPassLanes: "right-side pass width",
};

const SENSITIVITY_NAMES: Record<number, string> = {
    [CollisionSensitivity.Lowest]: "Lowest",
    [CollisionSensitivity.Low]: "Low",
    [CollisionSensitivity.Medium]: "Medium",
    [CollisionSensitivity.High]: "High",
    [CollisionSensitivity.Highest]: "Highest",
};

/** Each field's value in the units the screen shows it in. */
const ALERT_FIELD_FORMATTERS: Record<keyof AlertSettings, (value: number | boolean) => string> = {
    minApproachSpeedMps: (v) => `${Math.round((v as number) * MPS_TO_MPH)} mph`,
    speedingThresholdMps: (v) => `${Math.round((v as number) * MPS_TO_MPH)} mph`,
    sidePassMinSpeedMps: (v) => `${Math.round((v as number) * MPS_TO_MPH)} mph`,
    reactionTimeSeconds: (v) => `${(v as number).toFixed(1)} s`,
    lightsPulseSeconds: (v) => `${(v as number).toFixed(1)} s`,
    hornPulseSeconds: (v) => `${(v as number).toFixed(1)} s`,
    sensitivity: (v) => SENSITIVITY_NAMES[v as number] ?? String(v),
    speedingEnabled: (v) => (v ? "on" : "off"),
    leftPassEnabled: (v) => (v ? "on" : "off"),
    rightPassEnabled: (v) => (v ? "on" : "off"),
    leftPassLanes: (v) => `${v} lane${v === 1 ? "" : "s"}`,
    rightPassLanes: (v) => `${v} lane${v === 1 ? "" : "s"}`,
};

/**
 * The differing fields, each carrying both values.
 *
 * "This unit differs from the fleet default on minimum approach speed" answered which field but
 * left a director hunting through two screens for the numbers — and unable to tell a deliberate
 * per-truck choice from a fleet default that moved after the truck was set up. Naming both values
 * turns the flag into something actionable at a glance.
 */
export function describeAlertDifferences(current: AlertSettings, defaults: AlertSettings): string[] {
    return alertSettingsDifferences(current, defaults).map((key) => {
        const format = ALERT_FIELD_FORMATTERS[key];
        return `${ALERT_FIELD_LABELS[key]} (this truck ${format(current[key])} · fleet ${format(defaults[key])})`;
    });
}
